import { Flex, Grid, Table, Typography, theme } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import type { HistoryRow } from './historyApi'
import { actorLabel, kindLabel, packageLine, signedMoney } from './historyLabels'

function Amount({ value }: { value: Money }) {
  const { token } = theme.useToken()
  const color = value.amount > 0 ? token.colorSuccess : value.amount < 0 ? token.colorError : undefined
  return (
    <Typography.Text strong style={{ color, whiteSpace: 'nowrap' }}>
      {signedMoney(value)}
    </Typography.Text>
  )
}

function Operation({ row }: { row: HistoryRow }) {
  const { t } = useTranslation()
  const pkg = packageLine(row)
  return (
    <Flex vertical>
      <Typography.Text>{kindLabel(row.kind, t)}</Typography.Text>
      {pkg ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {pkg}
        </Typography.Text>
      ) : null}
      {row.note ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {row.note}
        </Typography.Text>
      ) : null}
    </Flex>
  )
}

function RowCard({ row }: { row: HistoryRow }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <div style={{ borderBottom: `1px solid ${token.colorSplit}`, paddingBlock: 10 }}>
      <Flex justify="space-between" align="start" gap={12}>
        <Operation row={row} />
        <Flex vertical align="end">
          <Amount value={row.amount} />
          <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {t('history.balanceAfterShort', { amount: formatMoneyValue(row.balanceAfter) })}
          </Typography.Text>
        </Flex>
      </Flex>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {[
          formatDateTime(row.at),
          row.reference ? t('history.documentShort', { no: row.reference }) : null,
          actorLabel(row.actor, t),
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography.Text>
    </div>
  )
}

export function HistoryRows({ rows }: { rows: readonly HistoryRow[] }) {
  const { t } = useTranslation()
  const isMobile = Grid.useBreakpoint().md === false

  if (isMobile) {
    return (
      <div>
        {rows.map((row) => (
          <RowCard key={row.txnId} row={row} />
        ))}
      </div>
    )
  }

  const columns: ColumnsType<HistoryRow> = [
    {
      key: 'at',
      title: t('history.col.at'),
      width: 170,
      render: (_v, row) => formatDateTime(row.at),
    },
    {
      key: 'operation',
      title: t('history.col.operation'),
      render: (_v, row) => <Operation row={row} />,
    },
    {
      key: 'amount',
      title: t('history.col.amount'),
      align: 'right',
      width: 140,
      render: (_v, row) => <Amount value={row.amount} />,
    },
    {
      key: 'balanceAfter',
      title: t('history.col.balanceAfter'),
      align: 'right',
      width: 140,
      render: (_v, row) => formatMoneyValue(row.balanceAfter),
    },
    {
      key: 'reference',
      title: t('history.col.document'),
      width: 150,
      ellipsis: true,
      render: (_v, row) => row.reference ?? t('common.dash'),
    },
    {
      key: 'actor',
      title: t('history.col.actor'),
      width: 170,
      ellipsis: true,
      render: (_v, row) => actorLabel(row.actor, t),
    },
  ]

  return (
    <Table<HistoryRow>
      rowKey="txnId"
      size="small"
      columns={columns}
      dataSource={[...rows]}
      pagination={false}
      scroll={{ x: 900 }}
    />
  )
}
